import type { DismissReason, RepBucket } from '../../../../shared/enums';
import type { Signal } from '../../../../shared/types';
import { shell } from '../../app';
import { NetworkError } from '../../data/api';
import {
  addSignalAsLead,
  dismissSignal,
  restoreSignal,
  setChain,
  setSignalBucket,
  updateStoredSignal,
} from '../../data/signals';
import type { AddResult } from '../../data/signals';
import { formatDate, todayLocal } from '../../logic/dates';
import { navigate } from '../../ui/router';
import { planStop } from './inboxPlan';

/** The inbox's triage (D57, D72, D74, D81): add, add + plan, dismiss with a reason, later, hide, show,
 *  not a chain — each acting on the list at once and telling the rep, with undo where it can. Add and
 *  Dismiss go through the outbox, so they act offline too; the rest need the server's answer. The inbox
 *  lends it the list (drop, move, load, forget) and keeps the state. */

export interface TriageHost {
  /** The row out of both lists, at once. */
  drop: (id: string) => void;
  /** The row on the other side of the fold, until the server answers. */
  move: (s: Signal, to: 'all' | 'hidden') => void;
  load: () => Promise<void>;
  /** Out of the stored copy too, once the server has the triage (D65). */
  forget: (id: string) => void;
  /** The row back on the page, where its bucket says, on undo (D81). */
  put: (s: Signal) => void;
}

export interface Triage {
  add: (s: Signal) => Promise<void>;
  addAndPlan: (s: Signal) => Promise<void>;
  dismiss: (s: Signal, reason?: DismissReason) => Promise<void>;
  later: (s: Signal) => Promise<void>;
  hide: (s: Signal) => Promise<void>;
  show: (s: Signal) => Promise<void>;
  notAChain: (s: Signal) => Promise<void>;
}

const addedMessage = (name: string, r: AddResult): string =>
  r.offline
    ? `${r.existed ? `${name} is already a lead` : `Added ${name} on this phone`}; the inbox follows when you are online. Tap to open`
    : r.linked
      ? r.existed
        ? `${name} was already a lead — linked. Tap to open`
        : `Added ${name} — tap to open`
      : `${r.existed ? `${name} is already a lead` : `Added ${name}`}, but the inbox could not record it. Tap to open`;

const failed = (err: unknown): string => (err instanceof Error ? err.message : String(err));
const couldNot = (what: string, name: string) => shell.toast(`Could not ${what} ${name}.`, { kind: 'error' });
/** "Dismissing needs a connection." offline; "Could not dismiss Alpha Cafe." for anything else. */
const needs = (err: unknown, doing: string, what: string, name: string) =>
  err instanceof NetworkError
    ? shell.toast(`${doing} needs a connection.`, { kind: 'error' })
    : couldNot(what, name);

export const createTriage = ({ drop, move, load, forget, put }: TriageHost): Triage => {
  /** Undo's first half: the row back on the page and in the stored copy at once, server or not (D81). */
  const back = (s: Signal) => {
    put(s);
    void updateStoredSignal({ ...s, status: 'new' }).catch(() => undefined);
  };
  /** The lead added (or reused) and the signal linked; null result means the link failed. */
  const added = async (s: Signal): Promise<AddResult> => {
    const result = await addSignalAsLead(s, todayLocal());
    if (result.linked) {
      forget(s.id);
      drop(s.id); // a reload that landed meanwhile may have listed it again
    }
    return result;
  };

  const add = async (s: Signal) => {
    drop(s.id);
    try {
      const result = await added(s);
      shell.toast(addedMessage(s.business_name, result), {
        duration: 6000,
        onClick: () => navigate(`#/leads/${encodeURIComponent(result.leadId)}`),
      });
    } catch (err) {
      shell.toast(`Could not add ${s.business_name}: ${failed(err)}`, { kind: 'error' });
      void load();
    }
  };

  /** Add, then the stop on the day's route with its lead and the why (D74); undo takes the stop back
   *  and the signal returns to the inbox, the lead stays. */
  const addAndPlan = async (s: Signal) => {
    drop(s.id);
    try {
      const result = await added(s);
      const open = () => navigate(`#/leads/${encodeURIComponent(result.leadId)}`);
      const planned = result.linked ? await planStop(s, result.leadId) : null;
      if (!planned) {
        const text = result.offline
          ? `${result.existed ? `${s.business_name} is already a lead` : `Added ${s.business_name} on this phone`}; could not place it on the map — plan it from the lead. The inbox follows when you are online. Tap to open`
          : result.linked
            ? `Added ${s.business_name}; could not place it on the map — plan it from the lead. Tap to open`
            : addedMessage(s.business_name, result);
        shell.toast(text, { duration: 6000, onClick: open });
        return;
      }
      shell.toast(`Added · stop ${planned.index} on your route — tap to undo`, {
        duration: 8000,
        onClick: () => {
          planned.undo();
          back(s);
          void restoreSignal(s.id, s.business_name)
            .then(load)
            .catch(() => couldNot('bring back', s.business_name));
        },
      });
    } catch (err) {
      shell.toast(`Could not add ${s.business_name}: ${failed(err)}`, { kind: 'error' });
      void load();
    }
  };

  const dismiss = async (s: Signal, reason?: DismissReason) => {
    drop(s.id);
    try {
      const result = await dismissSignal(s.id, reason, s.business_name);
      forget(s.id);
      drop(s.id);
      const learned = 'learned' in result ? result.learned?.replace(/^chain:/, '') : undefined;
      const restore = () =>
        learned
          ? setChain(s.id, false).then(() => restoreSignal(s.id, s.business_name))
          : restoreSignal(s.id, s.business_name);
      shell.toast(
        learned
          ? `Dismissed. ${learned} is hidden from now on — tap to undo`
          : `Dismissed ${s.business_name} — tap to undo`,
        {
          duration: 8000,
          onClick: () => {
            back(s);
            void restore()
              .then(load)
              .catch(() => couldNot('bring back', s.business_name));
          },
        },
      );
      if (learned) await load(); // its other matches are hidden now
    } catch (err) {
      needs(err, 'Dismissing', 'dismiss', s.business_name);
      void load();
    }
  };

  /** The rep's own bucket (D72), with the undo toast when one is given; the list follows the server. The
   *  undo gives the bucket back to the sort (D75), never the opposite pin. */
  const rebucket = async (
    s: Signal,
    bucket: RepBucket,
    reason: 'later' | undefined,
    verb: 'hide' | 'show',
    said?: (fresh: Signal) => string,
  ) => {
    move(s, bucket === 'hidden' ? 'hidden' : 'all');
    try {
      const fresh = await setSignalBucket(s.id, bucket, reason);
      await updateStoredSignal(fresh).catch(() => undefined);
      if (said)
        shell.toast(said(fresh), {
          duration: 8000,
          // back to the bucket the rep had set before, else to the sort's own verdict
          onClick: () =>
            void setSignalBucket(s.id, s.rep_bucket ?? null)
              .then(load)
              .catch(() => couldNot('bring back', s.business_name)),
        });
      await load();
    } catch (err) {
      needs(err, verb === 'hide' ? 'Hiding' : 'Showing', verb, s.business_name);
      void load();
    }
  };

  return {
    add,
    addAndPlan,
    dismiss,
    later: (s) =>
      rebucket(
        s,
        'hidden',
        'later',
        'hide',
        (fresh) =>
          `Hidden until ${fresh.hidden_until ? formatDate(fresh.hidden_until.slice(0, 10)) : 'later'} — tap to undo`,
      ),
    hide: (s) => rebucket(s, 'hidden', undefined, 'hide', () => `Hidden ${s.business_name} — tap to undo`),
    show: (s) => rebucket(s, 'queue', undefined, 'show'),
    notAChain: async (s) => {
      try {
        await setChain(s.id, false);
        shell.toast(`Not a chain: ${s.business_name} and its matches are back.`);
        await load();
      } catch (err) {
        needs(err, 'Taking a chain off the list', 'take off the chain list', s.business_name);
      }
    },
  };
};
